'use client';

import type { ApiKey, ApiKeyStatus, RateLimitTier } from '@interfaces/apiKey';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { Button, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo } from 'react';
import { formatDate, formatDateTime } from './formatDate';

/** Requests per minute, mirroring `RATE_LIMIT_TIER_CONFIG` in the API's key model. */
const TIER_LABELS: Record<RateLimitTier, string> = {
  pro: 'Pro — 600 req/min',
  partner: 'Partner — 6,000 req/min',
  enterprise: 'Enterprise — 30,000 req/min',
};

type StatusPresentation = {
  color: 'success' | 'warning' | 'default';
  label: string;
  tooltip?: string;
};

/**
 * Shown for a `status` that is not one of the three we know about — including a
 * key from a deployment older than the field itself, where it is absent
 * entirely.
 *
 * This exists because the alternative is a blank screen. A missing status used
 * to be unrepresentable (the table branched on a boolean), and reading an
 * unknown one straight out of a lookup would throw during render and take the
 * whole page with it — a key nobody can see is a key nobody can revoke, which is
 * far worse than a key labelled "Unknown".
 */
const UNKNOWN_STATUS: StatusPresentation = {
  color: 'default',
  label: 'Unknown',
  tooltip: 'This key’s status could not be read. Reload, and contact support if it persists.',
};

/**
 * The API resolves three states from two independent facts and sends the answer
 * as `status`, so this reads it rather than deriving it again.
 *
 * `expired` is a state this table could not previously show at all — it was
 * typed against a boolean `enabled`, so an expired key rendered as merely
 * "disabled", pointing its owner at a subscription that was never the problem.
 */
function presentStatus(key: ApiKey): StatusPresentation {
  const byStatus: Record<ApiKeyStatus, StatusPresentation> = {
    active: { color: 'success', label: 'Active' },
    expired: {
      color: 'default',
      label: 'Expired',
      tooltip: 'This key reached its expiry date. Expiry is final — create a new key to replace it.',
    },
    disabled: {
      color: 'warning',
      label: 'Disabled',
      tooltip:
        key.disabledReason === 'entitlement_lapsed'
          ? 'Disabled because the Pro subscription lapsed. Re-subscribing restores it.'
          : 'Disabled. Contact support if you did not expect this.',
    },
  };

  return byStatus[key.status] ?? UNKNOWN_STATUS;
}

type Props = {
  keys: ApiKey[];
  onRevoke: (key: ApiKey) => void;
  /** True while the list is refetching, so a revoke that just landed reads as in-flight. */
  isRefreshing: boolean;
};

const ApiKeysTable: React.FC<Props> = ({ keys, onRevoke, isRefreshing }) => {
  const columns = useMemo<ColumnsType<ApiKey>>(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        // Nullable: a key edited in the Unkey console can lose its label, and a
        // key with no name is still a key its owner has to be able to revoke.
        render: (name: string | null) =>
          name === null ? (
            <span className={styles.muted}>Unnamed</span>
          ) : (
            <span className={styles.keyName}>{name}</span>
          ),
      },
      {
        title: 'Key',
        dataIndex: 'prefix',
        key: 'prefix',
        render: (prefix: string) => (
          <Tooltip title="The start of the key. The full value is shown only when the key is created.">
            <span className={styles.keyPrefix}>{prefix}…</span>
          </Tooltip>
        ),
      },
      {
        title: 'Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        responsive: ['md'],
        render: (createdAt: string) => formatDate(createdAt) ?? <span className={styles.muted}>Unknown</span>,
      },
      {
        title: 'Last used',
        dataIndex: 'lastUsedAt',
        key: 'lastUsedAt',
        responsive: ['lg'],
        render: (lastUsedAt: string | null) =>
          formatDateTime(lastUsedAt) ?? <span className={styles.muted}>Never</span>,
      },
      {
        title: 'Status',
        key: 'status',
        render: (_, key) => {
          const { color, label, tooltip } = presentStatus(key);
          const tag = (
            <Tag color={color} variant="filled">
              {label}
            </Tag>
          );

          return tooltip === undefined ? tag : <Tooltip title={tooltip}>{tag}</Tooltip>;
        },
      },
      {
        title: 'Rate limit',
        dataIndex: 'tier',
        key: 'tier',
        responsive: ['lg'],
        // Null on a key minted before the current metadata shape. It is still
        // enforced at the default tier, but saying which would be a guess.
        render: (tier: RateLimitTier | null) =>
          tier === null ? <span className={styles.muted}>—</span> : (TIER_LABELS[tier] ?? tier),
      },
      {
        title: '',
        key: 'actions',
        align: 'right',
        render: (_, key) => (
          <Button color="danger" variant="text" size="small" onClick={() => onRevoke(key)}>
            Revoke
          </Button>
        ),
      },
    ],
    [onRevoke],
  );

  return (
    <div className={styles.tableWrapper}>
      <Table<ApiKey>
        rowKey="id"
        columns={columns}
        dataSource={keys}
        pagination={false}
        loading={isRefreshing}
        scroll={{ x: 'max-content' }}
        aria-label="API keys"
      />
    </div>
  );
};

export { ApiKeysTable };
